import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';

const ORGANIZATION_SIZE_OPTIONS = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-1000 employees',
  '1000+ employees'
];

const OrganizationList = () => {
  const { isDemoMode } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [industryOptions, setIndustryOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [syncingOrgId, setSyncingOrgId] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, organization: null, isNew: false });
  const [syncDialog, setSyncDialog] = useState({ open: false, organization: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, organization: null });
  const [deletingOrgId, setDeletingOrgId] = useState(null);

  useEffect(() => {
    fetchOrganizations();
    fetchIndustryOptions();
    fetchLanguageOptions();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const orgData = await adminService.getOrganizations();
      setOrganizations(orgData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustryOptions = async () => {
    try {
      const options = await adminService.getIndustries();
      setIndustryOptions(options);
    } catch (error) {
      console.error('Failed to load industry options:', error);
    }
  };

  const fetchLanguageOptions = async () => {
    try {
      const options = await adminService.getLanguages();
      setLanguageOptions(options);
    } catch (error) {
      console.error('Failed to load language options:', error);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleEditOrganization = (organization = null) => {
    setInfoMessage(null);

    const normalizeListField = (value) => {
      if (!value) return '';
      if (Array.isArray(value)) return value.join(', ');
      return value;
    };

    const baseOrganization = organization || {
      name: '',
      domains: [''],
      size: '',
      industry: '',
      ai_maturity_score: '',
      departments: '',
      context: ''
    };

    const normalizedDepartments = normalizeListField(
      baseOrganization.departments || baseOrganization.teams
    );

    setEditDialog({
      open: true,
      organization: {
        ...baseOrganization,
        departments: normalizedDepartments,
        teams: normalizedDepartments
      },
      isNew: !organization
    });
  };

  const handleDeleteOrganization = async (orgId) => {
    if (!orgId) return;
    try {
      setError(null);
      setInfoMessage(null);
      setDeletingOrgId(orgId);
      await adminService.deleteOrganization(orgId);
      await fetchOrganizations();
      setDeleteDialog({ open: false, organization: null });
      setInfoMessage('Organization deleted successfully.');
    } catch (error) {
      setError(error.message);
    } finally {
      setDeletingOrgId(null);
    }
  };

  const handleSaveOrganization = async () => {
    try {
      setError(null);
      setInfoMessage(null);
      const orgData = { ...editDialog.organization };

      // Clean up empty domains
      orgData.domains = orgData.domains.filter(domain => domain.trim() !== '');
      if (typeof orgData.context === 'string') {
        orgData.context = orgData.context.trim();
      }

      if (typeof orgData.departments === 'string') {
        orgData.departments = orgData.departments.trim();
      }
      orgData.teams = orgData.departments;

      if (editDialog.isNew) {
        await adminService.createOrganization(orgData);
        setInfoMessage('Organization created successfully.');
      } else {
        await adminService.updateOrganization(editDialog.organization.id, orgData);
        setInfoMessage('Organization updated successfully.');
      }

      await fetchOrganizations();
      setEditDialog({ open: false, organization: null, isNew: false });
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSyncOrganization = async (orgId, orgName) => {
    try {
      setSyncingOrgId(orgId);
      setError(null);
      setInfoMessage(null);
      const result = await adminService.syncOrganizationUsers(orgId);
      const updatedUsers = result?.updated_users ?? 0;
      const suffix = updatedUsers === 1 ? 'user' : 'users';
      setInfoMessage(
        updatedUsers > 0
          ? `Updated ${updatedUsers} ${suffix} with the latest details for ${orgName || 'organization'}.`
          : `No user profile updates were required for ${orgName || 'organization'}.`
      );
    } catch (error) {
      setError(error.message);
    } finally {
      setSyncingOrgId(null);
    }
  };

  const handleConfirmSync = async () => {
    if (!syncDialog.organization) return;
    const { id, name } = syncDialog.organization;
    setSyncDialog({ open: false, organization: null });
    await handleSyncOrganization(id, name);
  };

  // Domain handling functions no longer needed - using comma-separated text field

  const formatDomains = (domains) => {
    if (!domains || domains.length === 0) return 'No domains';
    return domains.join(', ');
  };

  // Filter and sort organizations
  const filteredOrganizations = organizations
    .filter(org =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (org.domains && org.domains.some(domain => domain.toLowerCase().includes(searchTerm.toLowerCase())))
    )
    .sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (order === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading organizations...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load organizations: {error}
      </Alert>
    );
  }

  return (
    <Box>
      {infoMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {infoMessage}
        </Alert>
      )}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Organization Management ({organizations.length} organizations)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search organizations..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => !isDemoMode && handleEditOrganization()}
                disabled={isDemoMode}
                sx={{ backgroundColor: '#4b0000', '&:hover': { backgroundColor: '#6b0000' } }}
              >
                Add Organization
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Domains</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'size'}
                  direction={orderBy === 'size' ? order : 'asc'}
                  onClick={() => handleSort('size')}
                >
                  Size
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'industry'}
                  direction={orderBy === 'industry' ? order : 'asc'}
                  onClick={() => handleSort('industry')}
                >
                  Industry
                </TableSortLabel>
              </TableCell>
              <TableCell>AI Maturity</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrganizations.map((org) => (
              <TableRow key={org.id} hover>
                <TableCell>{org.name}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatDomains(org.domains)}
                  </Typography>
                </TableCell>
                <TableCell>{org.size || ''}</TableCell>
                <TableCell>{org.industry || ''}</TableCell>
                <TableCell>
                  {org.ai_maturity_score && (
                    <Chip
                      label={`${org.ai_maturity_score}/5`}
                      size="small"
                      color="primary"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : "Edit Organization"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => !isDemoMode && handleEditOrganization(org)}
                          disabled={isDemoMode}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : "Sync Users"}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={syncingOrgId === org.id || isDemoMode}
                          onClick={() => !isDemoMode && setSyncDialog({ open: true, organization: org })}
                        >
                          {syncingOrgId === org.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SyncIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : "Delete Organization"}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => !isDemoMode && setDeleteDialog({ open: true, organization: org })}
                          disabled={isDemoMode}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredOrganizations.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No organizations found matching your search.
          </Typography>
        </Box>
      )}

      {/* Edit/Create Organization Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, organization: null, isNew: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {editDialog.isNew ? 'Add Organization' : 'Edit Organization'}
            </Typography>
            {editDialog.organization?.name && (
              <Chip
                label={editDialog.organization.name}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* First Row: Org Name, Industry, Domains */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Organization Name"
                value={editDialog.organization?.name || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  organization: { ...editDialog.organization, name: e.target.value }
                })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={editDialog.organization?.industry || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    organization: { ...editDialog.organization, industry: e.target.value }
                  })}
                >
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  {industryOptions.length > 0 ? (
                    industryOptions.map(industry => (
                      <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No industries configured
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>Primary industry sector (optional)</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Domains"
                placeholder="example.com, company.org"
                value={editDialog.organization?.domains?.join(', ') || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  organization: {
                    ...editDialog.organization,
                    domains: e.target.value.split(',').map(d => d.trim()).filter(d => d)
                  }
                })}
                helperText="Comma-separated domain list"
              />
            </Grid>

            {/* Second Row: Teams/Deps, Size, Default Language */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Teams / Departments"
                placeholder="e.g., Product, Data Science, HR"
                value={editDialog.organization?.departments || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  organization: { ...editDialog.organization, departments: e.target.value, teams: e.target.value }
                })}
                helperText="Comma-separated list for profile dropdown"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Organization Size</InputLabel>
                <Select
                  value={editDialog.organization?.size || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    organization: { ...editDialog.organization, size: e.target.value }
                  })}
                >
                  {ORGANIZATION_SIZE_OPTIONS.map(size => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>Employee count range</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Default Language</InputLabel>
                <Select
                  value={editDialog.organization?.default_language || 'en'}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    organization: { ...editDialog.organization, default_language: e.target.value }
                  })}
                >
                  {languageOptions.length > 0 ? (
                    languageOptions.map(lang => (
                      <MenuItem key={lang.code} value={lang.code}>{lang.name}</MenuItem>
                    ))
                  ) : (
                    <MenuItem value="en">English</MenuItem>
                  )}
                </Select>
                <FormHelperText>Default interview language for new users</FormHelperText>
              </FormControl>
            </Grid>

            {/* Third Row: AI Maturity Level */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>AI Maturity Level</InputLabel>
                <Select
                  value={editDialog.organization?.ai_maturity_score || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    organization: { ...editDialog.organization, ai_maturity_score: e.target.value }
                  })}
                >
                  {[1, 2, 3, 4, 5].map(score => (
                    <MenuItem key={score} value={score}>{score}/5</MenuItem>
                  ))}
                </Select>
                <FormHelperText>Current AI maturity level</FormHelperText>
              </FormControl>
            </Grid>

            {/* Fourth Row: Organization Context (Full Width) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Organization Context"
                placeholder="e.g., Global tech company specializing in AI-driven healthcare solutions, with offices in 15 countries and a focus on medical data analytics and patient care optimization."
                value={editDialog.organization?.context || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  organization: { ...editDialog.organization, context: e.target.value }
                })}
                helperText="Shared internally with interview agents to personalize guidance."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, organization: null, isNew: false })}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveOrganization}
            variant="contained"
            disabled={!editDialog.organization?.name?.trim() || !editDialog.organization?.domains?.some(d => d.trim())}
            sx={{ backgroundColor: '#4b0000', '&:hover': { backgroundColor: '#6b0000' } }}
          >
            {editDialog.isNew ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Confirmation Dialog */}
      <Dialog
        open={syncDialog.open}
        onClose={() => setSyncDialog({ open: false, organization: null })}
      >
        <DialogTitle>Confirm Sync Users</DialogTitle>
        <DialogContent>
          <Typography>
            Sync user profiles for <strong>{syncDialog.organization?.name}</strong> with the latest organization data?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This updates stored user details (e.g., organization name, departments) for members of this organization.
            No user accounts are deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialog({ open: false, organization: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSync}
            variant="contained"
            disabled={syncingOrgId === syncDialog.organization?.id}
            sx={{ backgroundColor: '#4b0000', '&:hover': { backgroundColor: '#6b0000' } }}
          >
            {syncingOrgId === syncDialog.organization?.id ? <CircularProgress size={20} /> : 'Sync Users'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, organization: null })}
      >
        <DialogTitle>Confirm Delete Organization</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete organization <strong>{deleteDialog.organization?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This removes the organization record and its domain mappings. User accounts remain, but they will no longer
            be automatically associated with this organization. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, organization: null })}
            disabled={Boolean(deletingOrgId)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDeleteOrganization(deleteDialog.organization?.id)}
            disabled={deletingOrgId === deleteDialog.organization?.id}
          >
            {deletingOrgId === deleteDialog.organization?.id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationList;
